import { c as _c } from "react/compiler-runtime";
import { loader } from '@monaco-editor/react';
import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext } from 'react';
import { defineTemplateJsonTheme, registerTemplateLanguage } from '../template-editor/utils/monaco-config';
import { decorationAndSuggestionStyles } from '../template-editor/utils/styling-utils';
import { overrideMonacoKeybindings } from '../template-editor/utils/template-editor-utils';
import { ensureTemplateProvider } from '../template-editor/utils/template-utils';
import { ensureExpressionProvider, registerExpressionLanguage } from './model-autocomplete';
import { ensurePythonProvider } from './python-autocomplete';
import { registerExpressionThemes } from './themes';

// Monaco Editor loads from cdn.jsdelivr.net/npm/monaco-editor@0.52.2/min/vs
// See CSP documentation in middleware.page.ts for security considerations

const SUGGEST_STYLES_ID = 'monaco-suggest-styles';
const injectSuggestStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SUGGEST_STYLES_ID)) return;
  const style = document.createElement('style');
  style.id = SUGGEST_STYLES_ID;
  style.textContent = decorationAndSuggestionStyles;
  document.head.appendChild(style);
};

/** The clearance Monaco itself leaves at the window edge. */
const VIEWPORT_GUTTER = 15;
let placementRegistered = false;

/**
 * Monaco keeps the suggest widget on screen using the width it computed for itself, not the wider
 * one `decorationAndSuggestionStyles` asks for, so the right-hand end of the dropdown — where the
 * data type sits — hangs off the window.
 */
const keepSuggestWidgetOnScreen = (monaco: typeof import('monaco-editor')) => {
  if (placementRegistered) return;
  placementRegistered = true;
  monaco.editor.onDidCreateEditor(editor => {
    const observer = new MutationObserver(records => {
      for (const {
        target
      } of records) {
        if (!(target instanceof HTMLElement) || !target.classList.contains('suggest-widget')) continue;
        const left = Number.parseFloat(target.style.left);
        if (Number.isNaN(left)) continue;
        const spill = target.getBoundingClientRect().right - (document.documentElement.clientWidth - VIEWPORT_GUTTER);
        if (spill >= 1) target.style.left = `${left - spill}px`;
      }
    });
    observer.observe(editor.getContainerDomNode(), {
      attributes: true,
      attributeFilter: ['style'],
      subtree: true
    });
    editor.onDidDispose(() => observer.disconnect());
  });
};
export const initMonaco = async () => {
  // Storybook sets this from `@onefootprint/ui/monaco-local` so captures don't race the CDN
  // fetch; loader.init() then resolves from the instance instead of injecting its script tag.
  const bundled = (globalThis as Record<string, unknown>).__BUNDLED_MONACO__ as typeof import('monaco-editor') | undefined;
  if (bundled) loader.config({
    monaco: bundled
  });
  const monaco = await loader.init();
  defineTemplateJsonTheme(monaco);
  registerTemplateLanguage(monaco);
  registerExpressionLanguage(monaco);
  registerExpressionThemes(monaco);
  ensureTemplateProvider(monaco);
  ensureExpressionProvider(monaco);
  ensurePythonProvider(monaco);
  overrideMonacoKeybindings(monaco);
  injectSuggestStyles();
  keepSuggestWidgetOnScreen(monaco);
  return monaco;
};
type MonacoContextType = {
  monaco: typeof import('monaco-editor') | null;
  isLoading: boolean;
  error: Error | null;
};
const MonacoContext = createContext<MonacoContextType>({
  monaco: null,
  isLoading: true,
  error: null
});
export const MonacoProvider = t0 => {
  const $ = _c(9);
  const {
    children,
    languageName
  } = t0;
  let t1;
  if ($[0] !== languageName) {
    t1 = {
      queryKey: ["monaco-editor", languageName],
      queryFn: _temp,
      retry: false,
      staleTime: Number.POSITIVE_INFINITY
    };
    $[0] = languageName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: monaco,
    isLoading,
    error
  } = useQuery(t1);
  const t2 = monaco ?? null;
  const t3 = error as Error | null;
  let t4;
  if ($[2] !== isLoading || $[3] !== t2 || $[4] !== t3) {
    t4 = {
      monaco: t2,
      isLoading,
      error: t3
    };
    $[2] = isLoading;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== children || $[7] !== t4) {
    t5 = <MonacoContext.Provider value={t4}>{children}</MonacoContext.Provider>;
    $[6] = children;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
export const useMonaco = () => {
  const context = useContext(MonacoContext);
  if (!context) {
    throw new Error("useMonaco must be used within a MonacoProvider");
  }
  return context;
};
function _temp() {
  return initMonaco();
}